import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import RequestStatusBadge from '../../../components/request-builder/RequestStatusBadge';
import PaymentSummary from '../../../components/payments/PaymentSummary';
import RequestItemGroups from '../../../components/request-builder/RequestItemGroups';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useNotifications } from '../../../components/context/NotificationContext';
import { needsPriceFinalization } from '../../../utils/pricingUtils';

const getApiErrorMessage = (error, fallback) => {
  if (error?.data?.message) return error.data.message;
  if (typeof error?.data === 'string' && error.data.trim()) return error.data;
  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return fallback;
};

const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GH', options).format(date);
};

const getStageState = (instance, stage) => {
  const finalDecision = [...(instance.decisions || [])]
    .reverse()
    .find((decision) => (
      decision.stageOrder === stage.order &&
      ['approved', 'rejected'].includes(String(decision.decision || '').toLowerCase())
    ));
  if (finalDecision) return String(finalDecision.decision).toLowerCase();
  if (
    String(instance.status || '').toLowerCase() === 'inprogress' &&
    stage.order === instance.currentStageOrder
  ) return 'current';
  return 'pending';
};

const StageMarker = ({ state, number, compact = false }) => {
  const markerSize = compact ? 'h-6 w-6' : 'h-8 w-8';
  const iconSize = compact ? 12 : 16;

  if (state === 'approved') {
    return <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full bg-success text-white`}><Icon name="Check" size={iconSize} /></span>;
  }
  if (state === 'rejected') {
    return <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full bg-error text-white`}><Icon name="X" size={iconSize} /></span>;
  }
  return (
    <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full border text-xs font-caption font-semibold ${
      state === 'current'
        ? compact ? 'border-primary bg-card text-primary' : 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground'
    }`}>
      {number}
    </span>
  );
};

const ActivityRow = ({ icon, label, value, tone = 'primary' }) => {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    secondary: 'bg-secondary/10 text-secondary',
  };

  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${toneClasses[tone] || toneClasses.primary}`}>
        <Icon name={icon} size={17} />
      </span>
      <div className="min-w-0 pt-0.5">
        <p className="text-xs text-muted-foreground font-caption">{label}</p>
        <p className="mt-0.5 break-words text-sm font-medium text-foreground font-caption">{value || 'Not provided'}</p>
      </div>
    </div>
  );
};

const STAGE_STATE_LABELS = {
  approved: 'Approved',
  rejected: 'Rejected',
  current: 'Current stage',
  pending: 'Pending',
};

const TaskWorkflowTracker = ({ instance, stages }) => {
  const [expandedStageOrder, setExpandedStageOrder] = useState(null);
  const expandedStage = stages.find((stage) => stage.order === expandedStageOrder);
  const expandedStageState = expandedStage ? getStageState(instance, expandedStage) : null;
  const expandedStageDecisions = expandedStage
    ? (instance.decisions || []).filter((decision) => decision.stageOrder === expandedStage.order)
    : [];

  useEffect(() => {
    setExpandedStageOrder(null);
  }, [instance.id]);

  const toggleStage = (stageOrder) => {
    setExpandedStageOrder((current) => current === stageOrder ? null : stageOrder);
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Workflow</h3>
        <RequestStatusBadge status={instance.status} showIcon={false} />
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
          <p className="truncate font-heading text-base font-semibold text-foreground">{instance.workflowName}</p>
          <span className="flex-shrink-0 text-xs text-muted-foreground font-caption">
            {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
          </span>
        </div>

        <ol className="mt-4 space-y-0 md:hidden">
          {stages.map((stage, index) => {
            const state = getStageState(instance, stage);
            const isExpanded = expandedStageOrder === stage.order;
            return (
              <li key={stage.id || stage.order} className="relative pb-3 last:pb-0">
                {index < stages.length - 1 && (
                  <span className="absolute left-[15px] top-8 h-[calc(100%-0.5rem)] w-px bg-border" aria-hidden="true" />
                )}
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => toggleStage(stage.order)}
                  className="relative z-10 flex w-full items-start gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <StageMarker state={state} number={index + 1} />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className={`truncate text-sm font-semibold font-caption ${isExpanded ? 'text-primary' : 'text-foreground'}`}>{stage.name}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground font-caption">{stage.assignedUserName}</p>
                  </div>
                  <Icon name="ChevronDown" size={15} className={`mt-2 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </li>
            );
          })}
        </ol>

        <div className="mt-3 hidden overflow-x-auto pb-1 pt-2 md:block">
          <ol
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(9rem, 1fr))`,
              minWidth: `${Math.max(stages.length * 144, 288)}px`,
            }}
          >
            {stages.map((stage, index) => {
              const state = getStageState(instance, stage);
              const previousState = index > 0 ? getStageState(instance, stages[index - 1]) : 'pending';
              const isExpanded = expandedStageOrder === stage.order;
              const leftConnectorClass = previousState === 'approved'
                ? 'bg-success'
                : previousState === 'rejected' ? 'bg-error' : 'bg-border';
              const rightConnectorClass = state === 'approved'
                ? 'bg-success'
                : state === 'rejected' ? 'bg-error' : 'bg-border';

              return (
                <li key={stage.id || stage.order} className="relative min-w-0">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggleStage(stage.order)}
                    className="relative z-10 flex w-full min-w-0 flex-col items-center text-center focus-visible:outline-none"
                  >
                    <span className="flex w-full items-center" aria-hidden="true">
                      <span className={`h-0.5 flex-1 rounded-full ${index === 0 ? 'invisible' : leftConnectorClass}`} />
                      <span className={`mx-2 flex-shrink-0 rounded-full ${isExpanded ? 'ring-2 ring-primary/25 ring-offset-1 ring-offset-[var(--color-card)]' : ''}`}>
                        <StageMarker state={state} number={index + 1} compact />
                      </span>
                      <span className={`h-0.5 flex-1 rounded-full ${index === stages.length - 1 ? 'invisible' : rightConnectorClass}`} />
                    </span>
                    <span className={`mt-1.5 max-w-full truncate px-2 text-[11px] font-medium font-caption ${isExpanded ? 'text-primary' : 'text-foreground'}`}>{stage.name}</span>
                    <span className="max-w-full truncate px-2 text-[10px] text-muted-foreground font-caption">{stage.assignedUserName}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        {expandedStage && (
          <div className="mt-4 rounded-lg border border-border/70 bg-card/80 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-foreground font-caption">{expandedStage.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground font-caption">Assigned to {expandedStage.assignedUserName}</p>
              </div>
              <span className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground font-caption">
                {STAGE_STATE_LABELS[expandedStageState] || 'Pending'}
              </span>
            </div>

            {expandedStageDecisions.length > 0 ? (
              <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
                {expandedStageDecisions.map((decision) => (
                  <div key={decision.id || `${decision.stageOrder}-${decision.createdAt}`} className="text-xs font-caption">
                    <p className="font-medium capitalize text-foreground">
                      {decision.decision} by {decision.decidedByName || 'Team member'}
                    </p>
                    {decision.createdAt && <p className="mt-0.5 text-muted-foreground">{formatDate(decision.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</p>}
                    {decision.comment && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{decision.comment}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 border-t border-border/60 pt-3 text-xs text-muted-foreground font-caption">
                No decisions or comments have been recorded for this stage.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export const WorkflowDecisionForm = ({ onSubmit, isSubmitting, inFooter = false, onClose, approvalDisabledReason = '' }) => {
  const [decision, setDecision] = useState('');
  const [comment, setComment] = useState('');
  const [validationError, setValidationError] = useState('');

  const options = [
    { value: 'approved', label: 'Approve', icon: 'Check', active: 'border-success bg-success/10 text-success' },
    { value: 'commented', label: 'Comment', icon: 'MessageSquare', active: 'border-primary bg-primary/10 text-primary' },
    { value: 'rejected', label: 'Reject', icon: 'X', active: 'border-error bg-error/10 text-error' },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationError('');
    if (!decision) {
      setValidationError('Choose an action before submitting.');
      return;
    }
    if (decision === 'approved' && approvalDisabledReason) {
      setValidationError(approvalDisabledReason);
      return;
    }
    if (['commented', 'rejected'].includes(decision) && !comment.trim()) {
      setValidationError(decision === 'rejected' ? 'Enter a rejection reason.' : 'Enter a comment.');
      return;
    }

    const succeeded = await onSubmit(decision, comment.trim());
    if (succeeded) {
      setDecision('');
      setComment('');
    }
  };

  const commentLabel = decision === 'rejected'
    ? 'Rejection reason'
    : decision === 'approved'
      ? 'Approval note'
      : 'Comment';

  return (
    <form onSubmit={handleSubmit} className={inFooter ? 'space-y-3' : 'rounded-xl bg-primary/5 p-4 md:p-5'}>
      <div className={inFooter ? 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between' : ''}>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Your Decision</h3>
          {!inFooter && (
            <p className="mt-1 text-sm text-muted-foreground font-caption">
              You are assigned to the current stage. Choose how this request should proceed.
            </p>
          )}
        </div>

        <div className={`${inFooter ? 'grid grid-cols-3 sm:w-auto' : 'mt-3 grid grid-cols-3'} gap-2`}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={decision === option.value}
              onClick={() => { setDecision(option.value); setValidationError(''); }}
              disabled={isSubmitting || (option.value === 'approved' && Boolean(approvalDisabledReason))}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors font-caption disabled:opacity-50 ${
                decision === option.value ? option.active : 'border-border bg-card text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={option.icon} size={15} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {validationError && (
        <div className={`${inFooter ? '' : 'mt-3'} rounded-lg border border-error/20 bg-error/10 p-2.5 text-sm text-error font-caption`}>
          {validationError}
        </div>
      )}

      {approvalDisabledReason && !validationError && (
        <div className={`${inFooter ? '' : 'mt-3'} rounded-lg border border-warning/20 bg-warning/10 p-2.5 text-xs text-foreground font-caption`}>
          {approvalDisabledReason}
        </div>
      )}

      {decision && (
        <div className={inFooter ? '' : 'mt-3'}>
          <label htmlFor="workflow-decision-comment" className="mb-2 block text-sm text-foreground font-caption font-medium">
            {commentLabel}
            {decision !== 'approved' && <span className="ml-1 text-destructive">*</span>}
            {decision === 'approved' && <span className="ml-1 text-muted-foreground">(optional)</span>}
          </label>
          <textarea
            id="workflow-decision-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={2}
            disabled={isSubmitting}
            placeholder={decision === 'rejected' ? 'Explain why this request cannot proceed...' : 'Add a note for the workflow history...'}
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      )}

      <div className={`${inFooter ? '' : 'mt-3'} flex items-center ${onClose ? 'justify-between' : 'justify-end'} gap-3`}>
        {onClose && (
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Close
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          variant={decision === 'rejected' ? 'danger' : decision === 'approved' ? 'success' : 'default'}
          loading={isSubmitting}
          disabled={!decision || (decision === 'approved' && Boolean(approvalDisabledReason))}
        >
          {decision === 'rejected' ? 'Reject Request' : decision === 'commented' ? 'Add Comment' : decision === 'approved' ? 'Approve Stage' : 'Submit Decision'}
        </Button>
      </div>
    </form>
  );
};

const WorkflowTaskDetailsModal = ({ instanceId, isOpen, onClose, onTasksChanged }) => {
  const { lastWorkflowEvent } = useNotifications();
  const [instance, setInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const loadDetails = useCallback(async ({ silent = false } = {}) => {
    if (!instanceId) return null;
    if (!silent) setIsLoading(true);
    setLoadError('');
    try {
      const response = await repositoryContainer.workflowInstance.getById(instanceId);
      setInstance(response);
      return response;
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Task details could not be loaded.'));
      return null;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    if (!isOpen || !instanceId) return;
    setInstance(null);
    setActionError('');
    setActionResult(null);
    loadDetails();
  }, [instanceId, isOpen, loadDetails]);

  useEffect(() => {
    if (!isOpen || !instanceId || !lastWorkflowEvent) return;
    const eventInstanceId = lastWorkflowEvent.data?.workflowInstanceId
      ?? lastWorkflowEvent.data?.WorkflowInstanceId;
    const eventSubjectId = lastWorkflowEvent.data?.subjectId
      ?? lastWorkflowEvent.data?.SubjectId
      ?? lastWorkflowEvent.data?.bookingRequestId
      ?? lastWorkflowEvent.data?.BookingRequestId;
    const isPriceEventForRequest = lastWorkflowEvent.eventName === 'BookingRequestPriceFinalized'
      && eventSubjectId === instance?.bookingRequest?.id;
    if (eventInstanceId === instanceId || isPriceEventForRequest) loadDetails({ silent: true });
  }, [instanceId, isOpen, lastWorkflowEvent?.sequence, loadDetails]);

  const request = instance?.bookingRequest;
  const items = useMemo(() => (request?.items || []).map((item, index) => ({
    ...item,
    id: item.serviceItemId || item.id || `${item.type || 'item'}-${index}`,
    price: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 1,
    type: item.type?.toString().toLowerCase() || 'item',
  })), [request?.items]);

  const orderedStages = useMemo(
    () => [...(instance?.stages || [])].sort((first, second) => first.order - second.order),
    [instance?.stages]
  );
  const activityEventCount = [request?.createdAt, request?.completedAt, request?.paymentCompletedAt]
    .filter(Boolean).length;
  const pricingPending = needsPriceFinalization(request);
  const finalStageOrder = orderedStages.reduce((highest, stage) => Math.max(highest, Number(stage.order) || 0), 0);
  const isFinalStage = Number(instance?.currentStageOrder) === finalStageOrder && finalStageOrder > 0;
  const approvalDisabledReason = pricingPending && isFinalStage
    ? 'Every price-range item must have a final price before the final workflow approval.'
    : '';

  const handleDecision = async (decision, comment) => {
    setIsSubmitting(true);
    setActionError('');
    setActionResult(null);
    try {
      const result = await repositoryContainer.bookingRequest.processWorkflowDecision(instanceId, decision, comment);
      let message = 'Decision recorded successfully.';
      if (result.isComplete) message = 'The final stage was approved and the request is complete.';
      else if (result.isRejected) message = 'The request was rejected and the workflow is closed.';
      else if (result.nextStageName) message = `Advanced to ${result.nextStageName}${result.nextAssigneeName ? `, assigned to ${result.nextAssigneeName}` : ''}.`;
      else if (decision === 'commented') message = 'Your comment was added to the current stage.';

      setActionResult({ decision, message });
      await Promise.all([
        loadDetails({ silent: true }),
        Promise.resolve(onTasksChanged?.()),
      ]);
      return true;
    } catch (error) {
      setActionError(getApiErrorMessage(error, 'The decision could not be processed.'));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={instance && request ? (
        <span className="block">
          <span className="block font-heading text-xl font-semibold text-foreground">Review Assigned Task</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-normal text-muted-foreground font-caption">#{request.referenceCode}</span>
            <RequestStatusBadge status={request.status} showIcon={false} compact />
          </span>
        </span>
      ) : 'Review Assigned Task'}
      maxWidth="max-w-5xl"
      compactHeader
      footer={instance && request ? (
        instance.canCurrentUserAct ? (
          <WorkflowDecisionForm
            onSubmit={handleDecision}
            isSubmitting={isSubmitting}
            inFooter
            onClose={handleClose}
            approvalDisabledReason={approvalDisabledReason}
          />
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground font-caption">
              {String(instance.status).toLowerCase() === 'inprogress'
                ? 'This task is now assigned to another team member.'
                : 'This workflow is finished and no further action is available.'}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={handleClose}>Close</Button>
          </div>
        )
      ) : null}
    >
      {isLoading ? (
        <div className="space-y-4" aria-label="Loading task details">
          <div className="h-24 animate-pulse rounded-xl bg-muted" />
          <div className="h-48 animate-pulse rounded-xl bg-muted" />
          <div className="h-36 animate-pulse rounded-xl bg-muted" />
        </div>
      ) : loadError && !instance ? (
        <div className="py-10 text-center">
          <Icon name="AlertCircle" size={34} color="var(--color-error)" className="mx-auto mb-3" />
          <p className="text-sm text-error font-caption">{loadError}</p>
          <Button variant="outline" size="sm" className="mt-4" iconName="RefreshCw" onClick={() => loadDetails()}>
            Try Again
          </Button>
        </div>
      ) : instance && request ? (
        <div className="space-y-6">
          <div className="grid items-stretch gap-4 md:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
            <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-caption">Customer information</p>
                <h2 className="mt-1 font-heading text-2xl font-semibold text-foreground">{request.clientName}</h2>
                <div className="mt-3 flex flex-col gap-2 text-sm text-foreground font-caption sm:flex-row sm:flex-wrap sm:gap-x-5">
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon name="Mail" size={15} className="flex-shrink-0 text-muted-foreground" />
                    <span className="break-all">{request.clientEmail || 'Email not provided'}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Icon name="Phone" size={15} className="flex-shrink-0 text-muted-foreground" />
                    <span>{request.clientPhone || 'Phone not provided'}</span>
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
              <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Payment</h3>
              <PaymentSummary record={request} totalAmount={request.totalAmount} />
            </section>
          </div>

          {actionResult && (
            <div className={`rounded-xl border p-4 text-sm font-caption ${
              actionResult.decision === 'rejected'
                ? 'border-error/20 bg-error/10 text-error'
                : actionResult.decision === 'commented'
                  ? 'border-primary/20 bg-primary/10 text-primary'
                  : 'border-success/20 bg-success/10 text-success'
            }`}>
              {actionResult.message}
            </div>
          )}

          {actionError && (
            <div className="flex items-start gap-2 rounded-xl border border-error/20 bg-error/10 p-4">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-error font-caption">{actionError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.75fr)]">
            <div className="p-4 sm:p-5 lg:pr-6">
              <section>
                <div className="mb-3">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Requested items</h3>
                    <p className="mt-1 text-xs text-muted-foreground font-caption">
                      {items.length} {items.length === 1 ? 'item' : 'items'} submitted for review
                    </p>
                  </div>
                </div>
                {items.length > 0 ? (
                  <div className="rounded-xl bg-muted/20 px-3">
                    <RequestItemGroups items={items} showImages detailsLayout dense standardThumbnail />
                  </div>
                ) : (
                  <div className="rounded-xl bg-muted/30 p-6 text-center text-sm text-muted-foreground font-caption">No item information is available.</div>
                )}
              </section>

            </div>

            <aside className="space-y-5 border-t border-border/60 bg-muted/10 p-4 sm:p-5 lg:border-l lg:border-t-0 lg:pl-6">
              {(request.requestedDate || request.requestedTime) && (
                <section className="rounded-xl bg-muted/25 p-4">
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Preferred schedule</h3>
                  <div className="divide-y divide-border">
                    {request.requestedDate && <ActivityRow icon="Calendar" label="Date" value={formatDate(request.requestedDate)} tone="success" />}
                    {request.requestedTime && <ActivityRow icon="Clock" label="Time" value={request.requestedTime} />}
                  </div>
                </section>
              )}

              {(request.note || request.companyNote) && (
                <section className="rounded-xl bg-muted/25 p-4">
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Notes</h3>
                  <div className="space-y-4">
                    {request.note && (
                      <div>
                        <p className="text-xs text-muted-foreground font-caption">Customer note</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{request.note}</p>
                      </div>
                    )}
                    {request.companyNote && (
                      <div className="rounded-lg bg-primary/10 p-3">
                        <p className="text-xs font-medium text-primary font-caption">Company response</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{request.companyNote}</p>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </aside>
          </div>

          <TaskWorkflowTracker instance={instance} stages={orderedStages} />

          <details className="group rounded-xl bg-muted/20">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground font-caption [&::-webkit-details-marker]:hidden">
              <span>Request activity &middot; {activityEventCount} {activityEventCount === 1 ? 'event' : 'events'}</span>
              <Icon name="ChevronDown" size={16} className="flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="divide-y divide-border border-t border-border/70 px-4 py-3">
              {request.createdAt && (
                <ActivityRow
                  icon="CalendarPlus"
                  label="Submitted"
                  value={formatDate(request.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                />
              )}
              {request.completedAt && (
                <ActivityRow
                  icon="CheckCircle"
                  label="Completed"
                  value={formatDate(request.completedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  tone="success"
                />
              )}
              {request.paymentCompletedAt && (
                <ActivityRow
                  icon="BadgeCheck"
                  label="Initial payment confirmed"
                  value={formatDate(request.paymentCompletedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  tone="secondary"
                />
              )}
            </div>
          </details>

          {request.history?.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Request history</h3>
              <ol className="space-y-0 px-1 py-2">
                {request.history.map((entry, index) => (
                  <li key={`${entry.timestamp}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                    {index < request.history.length - 1 && (
                      <span className="absolute left-[5px] top-4 h-[calc(100%-0.25rem)] w-px bg-border" aria-hidden="true" />
                    )}
                    <span className="relative z-10 mt-1.5 h-3 w-3 flex-shrink-0 rounded-full border-2 border-card bg-primary" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground font-caption">{entry.action}</p>
                      <p className="mt-1 text-xs text-muted-foreground font-caption">
                        {formatDate(entry.timestamp, { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

        </div>
      ) : null}
    </Modal>
  );
};

export default WorkflowTaskDetailsModal;
